import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { ArrowLeft, Ban, Play, Loader2, RefreshCcw, AlertCircle } from 'lucide-preact'
import { routes } from '@/utils/routes'
import { usePageHeader } from '@/hooks/usePageHeader'
import { useEmailBlastDetail } from '@/hooks/useEmailBlastDetail'
import { BLAST_STATE_DESCRIPTIONS } from '@/types/emailBlast'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { BlastStateBadge } from './BlastStateBadge'
import {
  CircuitBreakerSection,
  SendStatsSection,
  RampProgressSection,
  SplitTestSection,
  StateHistorySection,
  GateDecisionsSection,
} from './blastDetailSections'
import { BlastEngagementSection } from './BlastEngagementSection'
import { BlastRecipientsSection } from './BlastRecipientsSection'

interface Props {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  webId: string
  blastId: string
}

/**
 * Deep-linkable blast detail (ORB-384) — promoted from the old full-screen overlay to its own route
 * (mirrors how ORB-383 turned the contact flyout into a route). Keeps breaker / send-stats / ramp /
 * history / gate decisions and the resume/cancel controls; adds the Engagement section and the paged
 * recipient drill-down (each recipient deep-links to its contact route).
 */
export function BlastDetailPage({ clientCompanyId, workspaceId, campaignId, webId, blastId }: Props) {
  const location = useLocation()
  const { setHeader } = usePageHeader()
  const { blast, loading, error, actioning, load, resume, cancel } = useEmailBlastDetail({ workspaceId, blastId })
  const [confirm, setConfirm] = useState<'resume' | 'cancel' | null>(null)

  const scoped = routes.scoped(clientCompanyId, workspaceId)
  const broadcastsHref = `${scoped.web(campaignId, webId)}/email-sends`

  useEffect(() => {
    setHeader({
      items: [
        { text: 'Campaigns', href: scoped.campaignsIndex() },
        { text: 'Campaign', href: scoped.campaign(campaignId) },
        { text: 'Web', href: scoped.web(campaignId, webId) },
        { text: 'Broadcasts', href: broadcastsHref },
        { text: 'Blast' },
      ],
    })
  }, [clientCompanyId, workspaceId, campaignId, webId])

  const goBack = () => {
    try { location.route(broadcastsHref) } catch { if (typeof window !== 'undefined') window.history.back() }
  }

  if (loading && !blast) {
    return (
      <div className="p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300">
        <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        Loading blast…
      </div>
    )
  }

  if (error && !blast) {
    return (
      <div className="p-6 space-y-4">
        <button onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Broadcasts
        </button>
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!blast) return null

  const canResume = blast.State === 'Paused'
  const canCancel = blast.State !== 'Complete' && blast.State !== 'Failed' && blast.State !== 'Cancelled'

  return (
    <div className="p-6 space-y-8 max-w-5xl">
      {/* Header */}
      <div className="space-y-3">
        <button onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Broadcasts
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Email blast</h1>
              <BlastStateBadge state={blast.State} />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{BLAST_STATE_DESCRIPTIONS[blast.State]}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-50"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            {canCancel && (
              <button
                onClick={() => setConfirm('cancel')}
                disabled={actioning}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {actioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Cancel
              </button>
            )}
            {canResume && (
              <button
                onClick={() => setConfirm('resume')}
                disabled={actioning}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {actioning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Resume
              </button>
            )}
          </div>
        </div>
      </div>

      <CircuitBreakerSection blast={blast} />
      <SendStatsSection blast={blast} />
      <RampProgressSection blast={blast} />
      <SplitTestSection blast={blast} />
      <BlastEngagementSection engagement={blast.Engagement} />
      <BlastRecipientsSection
        workspaceId={workspaceId}
        clientCompanyId={clientCompanyId}
        campaignId={campaignId}
        webId={webId}
        blastId={blastId}
      />
      <StateHistorySection blast={blast} />
      <GateDecisionsSection blast={blast} />

      <ConfirmationModal
        isOpen={confirm === 'resume'}
        title="Resume blast"
        message="Resume sending to the remaining audience? Sending will continue from where it paused, subject to the same bounce and complaint limits."
        type="info"
        confirmText="Resume"
        loading={actioning}
        onConfirm={() => { setConfirm(null); void resume() }}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmationModal
        isOpen={confirm === 'cancel'}
        title="Cancel blast"
        message="Cancel this blast? Any unsent emails will not be sent. This cannot be undone."
        type="danger"
        confirmText="Cancel blast"
        cancelText="Keep sending"
        loading={actioning}
        onConfirm={() => { setConfirm(null); void cancel() }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  )
}
